import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import LoadingButton from "@mui/lab/LoadingButton";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import Alert from "@mui/material/Alert";
import BASE_URL from "../../../config/apiConfig";
import { saveUserToken } from "../../../utils/auth";
import "./CreateProfilePage.css";

export function CreateProfileViewPage() {
  const location = useLocation();
  const navigate = useNavigate();

  const store_email = location.state?.email;

  const [showPassword, setShowPassword] = useState(false);
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({
    phone: "",
    password: "",
  });
  const [serverError, setServerError] = useState("");

  useEffect(() => {
    if (!store_email) {
      navigate("/get-otp", { replace: true });
    }
  }, [store_email, navigate]);

  const validateForm = () => {
    const newErrors = {};

    const phoneRegex = /^[0-9]{10}$/;
    const strongPasswordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

    if (!phone.trim()) {
      newErrors.phone = "Phone number is required.";
    } else if (!phoneRegex.test(phone.trim())) {
      newErrors.phone = "Phone number must be exactly 10 digits.";
    }

    if (!password) {
      newErrors.password = "Password is required.";
    } else if (!strongPasswordRegex.test(password)) {
      newErrors.password =
        "Use at least 8 characters with uppercase, lowercase, number and special character.";
    }

    setErrors({
      phone: newErrors.phone || "",
      password: newErrors.password || "",
    });

    return Object.keys(newErrors).length === 0;
  };

  const handlePhoneChange = (event) => {
    const value = event.target.value.replace(/\D/g, "").slice(0, 10);
    setPhone(value);

    if (errors.phone) {
      setErrors((prev) => ({ ...prev, phone: "" }));
    }

    if (serverError) {
      setServerError("");
    }
  };

  const handlePasswordChange = (event) => {
    setPassword(event.target.value);

    if (errors.password) {
      setErrors((prev) => ({ ...prev, password: "" }));
    }

    if (serverError) {
      setServerError("");
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setServerError("");

    if (!validateForm()) return;

    setLoading(true);

    try {
      const response = await fetch(`${BASE_URL}/api/user/auth/profile-setup`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: store_email,
          phone: phone.trim(),
          password,
        }),
      });

      let data = {};
      try {
        data = await response.json();
      } catch {
        data = {};
      }

      if (!response.ok) {
        setServerError(
          data.message || "Profile setup failed. Please try again.",
        );
        return;
      }

      saveUserToken(data.token, store_email);

      const pendingRedirect = sessionStorage.getItem("auth_redirect");

      if (
        pendingRedirect &&
        pendingRedirect.startsWith("/") &&
        !pendingRedirect.startsWith("//")
      ) {
        sessionStorage.removeItem("auth_redirect");

        navigate(pendingRedirect, { replace: true });
        return;
      }

      navigate("/user/home", { replace: true });
    } catch {
      setServerError(
        "Unable to create your profile right now. Please check your connection and try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box className="farmology-auth-page farmology-create-profile-page">
      <Box className="farmology-auth-container farmology-create-profile-container">
        <Box className="farmology-auth-card farmology-create-profile-card">
          {/* =================================================
              FORM PANEL
          ================================================= */}
          <Box className="farmology-auth-form-panel farmology-create-form-panel">
            <div className="farmology-auth-brand">
              <div className="farmology-auth-logo-wrap">
                <img
                  src="/LOGOS/Farmology-Logo-e1687720251448.png"
                  alt="Farmology"
                  className="farmology-auth-logo"
                />
              </div>

              <Typography className="farmology-auth-eyebrow">
                COMPLETE YOUR ACCOUNT
              </Typography>

              <Typography component="h1" className="farmology-auth-title">
                Create your profile.
              </Typography>

              <Typography className="farmology-auth-description">
                Your email is verified. Add your phone number and create a
                secure password to finish setting up your Farmology account.
              </Typography>
            </div>

            {serverError && (
              <Alert severity="error" className="farmology-create-alert">
                {serverError}
              </Alert>
            )}

            <Box
              component="form"
              onSubmit={handleUpdateProfile}
              className="farmology-auth-form"
              noValidate
            >
              <TextField
                fullWidth
                label="Email address"
                value={store_email || ""}
                InputProps={{ readOnly: true }}
                className="farmology-auth-field farmology-readonly-field"
                variant="outlined"
              />

              <TextField
                fullWidth
                label="Phone number"
                value={phone}
                onChange={handlePhoneChange}
                error={Boolean(errors.phone)}
                helperText={errors.phone}
                className="farmology-auth-field"
                variant="outlined"
                type="tel"
                autoComplete="tel"
                inputProps={{
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                  maxLength: 10,
                }}
                disabled={loading}
              />

              <TextField
                fullWidth
                label="Create password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={handlePasswordChange}
                error={Boolean(errors.password)}
                helperText={errors.password}
                className="farmology-auth-field farmology-password-field"
                variant="outlined"
                autoComplete="new-password"
                disabled={loading}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        type="button"
                        onClick={() => setShowPassword((prev) => !prev)}
                        edge="end"
                        className="farmology-password-toggle"
                        aria-label={
                          showPassword ? "Hide password" : "Show password"
                        }
                        title={showPassword ? "Hide password" : "Show password"}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-input": {
                    color: "#102015 !important",
                    WebkitTextFillColor: "#102015 !important",
                    opacity: "1 !important",
                    fontFamily: '"Inter", sans-serif',
                    fontSize: "14px",
                    fontWeight: 500,
                    letterSpacing: showPassword ? "0.01em" : "0.12em",
                  },
                  "& .MuiOutlinedInput-input::placeholder": {
                    color: "#667267",
                    opacity: 1,
                  },
                }}
              />

              <div className="farmology-password-hint">
                <span>Password requirements</span>
                <p>
                  8+ characters · uppercase · lowercase · number · special
                  character
                </p>
                <small>Click the eye icon to show or hide your password.</small>
              </div>

              <LoadingButton
                fullWidth
                size="large"
                type="submit"
                variant="contained"
                loading={loading}
                disabled={loading || !store_email}
                className="farmology-auth-submit"
              >
                Create profile
                <span className="farmology-auth-arrow">→</span>
              </LoadingButton>

              <Typography className="farmology-auth-signin">
                Your verified email will remain linked to this account.
              </Typography>

              <Typography className="farmology-auth-footer-note">
                Farmology keeps your account information secure.
              </Typography>
            </Box>
          </Box>

          {/* =================================================
              VISUAL PANEL
          ================================================= */}
          <Box className="farmology-auth-visual-panel farmology-create-visual-panel">
            <div className="farmology-auth-visual-copy">
              <Typography className="farmology-auth-visual-eyebrow">
                FARMOLOGY PLATFORM
              </Typography>

              <Typography
                component="h2"
                className="farmology-auth-visual-title"
              >
                Set up your account. Start farming smarter.
              </Typography>

              <Typography className="farmology-auth-visual-text">
                Complete your profile to access Farmology tools built around
                crop intelligence, field insights and better farm decisions.
              </Typography>
            </div>

            <div className="farmology-profile-setup-visual" aria-hidden="true">
              <div className="farmology-profile-setup-card">
                <div className="farmology-profile-setup-top">
                  <span>PROFILE SETUP</span>
                  <strong>02 / 02</strong>
                </div>

                <div className="farmology-profile-progress">
                  <span />
                </div>

                <div className="farmology-profile-setup-fields">
                  <i />
                  <i />
                  <i />
                </div>
              </div>
            </div>

            <div className="farmology-auth-stat-card">
              <span className="farmology-auth-stat-label">ACCOUNT STATUS</span>
              <strong>Email verified · Profile ready</strong>
            </div>

            <div className="farmology-auth-circle" />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default CreateProfileViewPage;
